import { createContext, ReactNode, useContext, useEffect, useRef, useState } from 'react'
import { useHistory, useLocation } from 'react-router'
import LoginModal from 'shared/components/login-modal'
import UserContext from 'shared/contexts/user'
import { googleAnalyticsEvent, setGTADataLayer, trackPageView } from 'shared/util/analytics'
import styled from 'styled-components'
import { OverlayBlock } from './components/blocks'
import LegacyFileMap from './components/legacy-file'
import { useCreationAccess } from './hooks/use-creation-access'
import { getBasePathFromCreationTool, getCreationToolFromPath } from './routes'
import { CreationToolFrame, CreationToolSourceFile } from './types'
import CreationTool from './types/creation-tool'
import CreationToolOutputType from './types/output-type'
import CreationToolStep from './types/steps'
import CreationToolUserRestriction from './types/user-restriction'
import getCreationSteps from './utils/get-creation-steps'

const DragDim = styled(OverlayBlock)`
    background: rgba(0, 0, 0, 0.7);
    pointer-events: none;
    position: fixed;
`

export type CreationToolsContextProps = {
    tool: CreationTool
    sources: CreationToolSourceFile[]
    outputType: CreationToolOutputType
    steps: CreationToolStep[]
    stepFrames: CreationToolFrame[]
    currentStep: CreationToolStep | undefined
    currentAudioId: string
    userRestrictionAccess: CreationToolUserRestriction[]
    isDraggingFile: boolean
    isGifMakerBeta: boolean
    setFiles: (sources: CreationToolSourceFile[], outputType: CreationToolOutputType) => void
    setSources: (sources: CreationToolSourceFile[]) => void
    setOutputType: (outputType: CreationToolOutputType) => void
    setCurrentAudioId: (id: string) => void
    saveFrames: (frames: CreationToolFrame[], step: CreationToolStep) => void
    trackEvent: (category: CreationTool.GACategory | undefined, action: string, label: string, value?: string) => void
    setIsDraggingFile: (isDraggingFile: boolean) => void
    setShowLoginModal: (showLoginModal: boolean) => void
    setIsGifMakerBeta: (isGifMakerBeta: boolean) => void
}

export const CreationToolsContext = createContext({} as CreationToolsContextProps)

type Props = {
    apiKey?: string
    children: ReactNode
    submitUrl?: string
}

const CreationToolsContextManager = ({ apiKey, children, submitUrl }: Props) => {
    const { user } = useContext(UserContext)
    const [sources, setSources] = useState<CreationToolSourceFile[]>([])
    const [outputType, setOutputType] = useState<CreationToolOutputType>(CreationToolOutputType.Gif)
    const [steps, setSteps] = useState<CreationToolStep[]>([])
    const [currentAudioId, setCurrentAudioId] = useState<string>('')
    const [showLoginModal, setShowLoginModal] = useState<boolean>(false)
    const [isDraggingFile, setIsDraggingFile] = useState<boolean>(false)
    const [isGifMakerBeta, setIsGifMakerBeta] = useState<boolean>(false)
    const userRestrictionAccess = useCreationAccess()
    const { pathname } = useLocation()
    const { push } = useHistory()
    const tool = getCreationToolFromPath(pathname)
    const basePath = getBasePathFromCreationTool(tool)
    const lastPathnameRef = useRef<string>(basePath)
    const stepFramesRef = useRef({})
    const currentStep = steps.find((step) => {
        const route = CreationToolStep.route(step)
        return pathname.indexOf(route) > -1
    })

    useEffect(() => {
        // set user id dimension for ga
        setGTADataLayer({
            dimension: 'dimension8',
            value: user.id,
        })
    }, [user])

    useEffect(() => {
        // only track page view if path changes
        if (pathname !== lastPathnameRef.current) {
            // TODO: Make sure sticker maker fires as stickermaker and not gifmaker?
            lastPathnameRef.current = pathname
            trackPageView(pathname)
        }
    }, [pathname])

    const setFiles = async (sources: CreationToolSourceFile[], outputType: CreationToolOutputType) => {
        // if the tool requires login, show the login modal
        if (CreationTool.requiresLogin(tool) && !user.loggedIn) {
            setShowLoginModal(true)
            return
        }

        const steps = getCreationSteps(sources, tool, outputType, userRestrictionAccess)
        setSources(sources)
        setOutputType(outputType)
        setSteps(steps)
        stepFramesRef.current = {}

        if (steps.length) {
            const url = `${basePath}${CreationToolStep.route(steps[0])}`
            push(url)
        }
    }

    const saveFrames = (frames: CreationToolFrame[], step: CreationToolStep) => {
        if (!stepFramesRef.current) return

        stepFramesRef.current[step] = frames
    }

    const trackEvent = (category: CreationTool.GACategory, action: string, label: string, value: string) => {
        // some events are shared and only know their category via the route
        if (!category) {
            category = CreationTool.gaEventCategory(tool)
        }
        googleAnalyticsEvent({ category, action, label, value: value as any })
    }

    return (
        <CreationToolsContext.Provider
            value={{
                tool,
                sources,
                outputType,
                currentAudioId,
                steps,
                stepFrames: currentStep ? stepFramesRef.current[currentStep] || [] : [],
                currentStep,
                userRestrictionAccess,
                isDraggingFile,
                isGifMakerBeta,
                setIsGifMakerBeta,
                setFiles,
                setSources,
                setOutputType,
                setCurrentAudioId,
                setIsDraggingFile,
                setShowLoginModal,
                saveFrames,
                trackEvent,
            }}
        >
            {children}
            {tool === CreationTool.GifMaker && <LegacyFileMap apiKey={apiKey} submitUrl={submitUrl} />}
            {isDraggingFile && <DragDim />}
            {showLoginModal && <LoginModal />}
        </CreationToolsContext.Provider>
    )
}

export default CreationToolsContextManager
